const DEFAULT_TRACE_SAMPLE_RATE = 0.01;
const MAX_TRACE_SAMPLE_RATE = 0.1;
const MAX_REPLAY_SESSION_SAMPLE_RATE = 0.01;
const MAX_REPLAY_ON_ERROR_SAMPLE_RATE = 0.1;

export interface WebSentryEnvironment {
	sentryDsn?: string;
	sentryEnvironment?: string;
	sentryRelease?: string;
	sentryTracesSampleRate?: number | string;
	sentryReplayEnabled?: boolean | string;
	sentryReplaySessionSampleRate?: number | string;
	sentryReplayOnErrorSampleRate?: number | string;
}

export interface WebSentryConfig {
	enabled: boolean;
	dsn: string;
	environment: string;
	release?: string;
	traceSampleRate: number;
	replayEnabled: boolean;
	replaySessionSampleRate: number;
	replayOnErrorSampleRate: number;
}

function parseBooleanFlag(value: boolean | string | undefined): boolean {
	return value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true');
}

function parseSampleRate(value: number | string | undefined, fallback: number): number {
	if (value === undefined || value === '') {
		return fallback;
	}

	const parsed = Number(value);
	return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback;
}

export function buildWebSentryConfig(environment: WebSentryEnvironment): WebSentryConfig {
	const dsn = environment.sentryDsn?.trim() ?? '';
	const sentryEnvironment = environment.sentryEnvironment?.trim() || 'unknown';
	const replayEnabled = parseBooleanFlag(environment.sentryReplayEnabled);

	return {
		enabled: Boolean(dsn) && sentryEnvironment.toLowerCase() !== 'unknown',
		dsn,
		environment: sentryEnvironment,
		release: environment.sentryRelease?.trim() || undefined,
		traceSampleRate: Math.min(parseSampleRate(environment.sentryTracesSampleRate, DEFAULT_TRACE_SAMPLE_RATE), MAX_TRACE_SAMPLE_RATE),
		replayEnabled,
		replaySessionSampleRate: replayEnabled
			? Math.min(parseSampleRate(environment.sentryReplaySessionSampleRate, 0), MAX_REPLAY_SESSION_SAMPLE_RATE)
			: 0,
		replayOnErrorSampleRate: replayEnabled
			? Math.min(parseSampleRate(environment.sentryReplayOnErrorSampleRate, 0), MAX_REPLAY_ON_ERROR_SAMPLE_RATE)
			: 0
	};
}
